import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Users, BarChart3, ShieldCheck, Plus, Pause, Play, Trash2, X as XIcon, Activity, Calendar, FileText, Stethoscope, MessageSquare, Database, Mail, Shield, ListChecks, Zap } from "lucide-react";
import BackupPanel from "@/components/BackupPanel";
import MessagingPanel from "@/components/MessagingPanel";
import SecurityPanel from "@/components/SecurityPanel";
import FsePanel from "@/components/FsePanel";
import CompliancePanel from "@/components/CompliancePanel";
import AutomationsCenter from "@/components/AutomationsCenter";

function StatBox({ label, value, tone = "navy" }) {
  const t = {
    navy: "bg-brand-navy text-white",
    azure: "bg-brand-azure text-white",
    azureLight: "bg-[#9FD8EA] text-[#1E3A5F]",
    light: "bg-white text-brand-navy border border-[#E2E8F0]",
  };
  return (
    <div className={`${t[tone]} rounded-xl p-5`}>
      <div className="text-xs uppercase font-bold tracking-wider opacity-80">{label}</div>
      <div className="text-3xl font-extrabold mt-1.5">{value}</div>
    </div>
  );
}

function MediciTab() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "Demo123!" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/medici");
      setList(data);
    } catch (e) { toast.error(apiErr(e, "Errore caricamento")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Nome ed email obbligatori"); return; }
    try {
      await api.post("/admin/medici", form);
      toast.success("Medico creato");
      setOpen(false);
      setForm({ name: "", email: "", password: "Demo123!" });
      load();
    } catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  const setStatus = async (m, status) => {
    try {
      await api.patch(`/admin/medici/${m.id}/status`, { status });
      toast.success(`Account ${status === "active" ? "riattivato" : "sospeso"}`);
      load();
    } catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  const del = async (m) => {
    if (!window.confirm(`Eliminare definitivamente ${m.name}? L'account sarà disattivato.`)) return;
    try {
      await api.delete(`/admin/medici/${m.id}`);
      toast.success("Account eliminato");
      load();
    } catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-extrabold text-brand-navy">Account medici oculisti</h2>
          <p className="text-sm text-slate-500">Crea, sospendi, riattiva o elimina i profili medico abilitati alla piattaforma.</p>
        </div>
        <button data-testid="open-create-medico" onClick={() => setOpen(true)} className="bg-brand-orange hover:bg-brand-orangeDark text-white font-bold px-4 py-2.5 rounded-lg flex items-center gap-2"><Plus size={16} /> Nuovo medico</button>
      </div>

      {loading ? <div className="text-slate-500 py-8 text-center">Caricamento…</div> : (
        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden" data-testid="medici-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Medico</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Stato</th>
                <th className="text-right px-4 py-3">Visite</th>
                <th className="text-right px-4 py-3">Bozze</th>
                <th className="text-right px-4 py-3">Chiuse</th>
                <th className="text-right px-4 py-3">Referti</th>
                <th className="text-right px-4 py-3">Sessioni</th>
                <th className="text-right px-4 py-3">Ultimo accesso</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((m) => {
                const status = m.status || "active";
                return (
                  <tr key={m.id} data-testid={`medico-row-${m.id}`} className="border-t border-[#E2E8F0]">
                    <td className="px-4 py-3 font-bold text-brand-navy">{m.name}</td>
                    <td className="px-4 py-3 text-slate-600">{m.email}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${status === "active" ? "bg-emerald-100 text-emerald-700" : status === "suspended" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-500"}`}>
                        {status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{m.stats.visits_total}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{m.stats.visits_draft}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{m.stats.visits_closed}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{m.stats.reports_sent}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{m.stats.total_sessions || 0}</td>
                    <td className="px-4 py-3 text-right text-slate-500 text-xs">{m.stats.last_login_at ? new Date(m.stats.last_login_at).toLocaleString("it-IT") : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {status === "active" && <button data-testid={`suspend-${m.id}`} onClick={() => setStatus(m, "suspended")} className="text-amber-600 hover:bg-amber-50 p-1.5 rounded" title="Sospendi"><Pause size={14} /></button>}
                        {status !== "active" && status !== "deleted" && <button data-testid={`reactivate-${m.id}`} onClick={() => setStatus(m, "active")} className="text-emerald-600 hover:bg-emerald-50 p-1.5 rounded" title="Riattiva"><Play size={14} /></button>}
                        {status !== "deleted" && <button data-testid={`delete-${m.id}`} onClick={() => del(m)} className="text-red-600 hover:bg-red-50 p-1.5 rounded" title="Elimina"><Trash2 size={14} /></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {list.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">Nessun medico</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="create-medico-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-extrabold text-brand-navy">Nuovo medico</h3>
              <button onClick={() => setOpen(false)}><XIcon /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Nome completo</label>
                <input data-testid="new-medico-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Dott./Dott.ssa..." className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</label>
                <input data-testid="new-medico-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="nome.cognome@primevision.it" className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Password iniziale</label>
                <input data-testid="new-medico-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100">Annulla</button>
              <button data-testid="confirm-create-medico" onClick={create} className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-5 py-2 rounded-lg">Crea account</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <div className="text-slate-500">Caricamento statistiche…</div>;
  const max = Math.max(...s.trend_7d.map((t) => t.visits), 1);
  return (
    <div>
      <h2 className="text-xl font-extrabold text-brand-navy mb-4">Statistiche piattaforma</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatBox label="Medici totali" value={s.medici.total} tone="navy" />
        <StatBox label="Attivi" value={s.medici.active} tone="azure" />
        <StatBox label="Sospesi" value={s.medici.suspended} tone="azureLight" />
        <StatBox label="Eliminati" value={s.medici.deleted} tone="light" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatBox label="Visite totali" value={s.visits.total} tone="navy" />
        <StatBox label="Bozze" value={s.visits.draft} tone="azureLight" />
        <StatBox label="Chiuse" value={s.visits.closed} tone="azure" />
        <StatBox label="Referti inviati" value={s.visits.reports_sent} tone="light" />
      </div>
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-5">
        <div className="flex items-center gap-2 mb-3"><Activity className="text-brand-azure" size={18} /><div className="font-extrabold text-brand-navy">Trend visite (7 giorni)</div></div>
        <div className="flex items-end gap-2 h-40">
          {s.trend_7d.map((t) => (
            <div key={t.date} className="flex-1 flex flex-col items-center gap-1.5">
              <div className="text-[10px] font-bold text-slate-600 tabular-nums">{t.visits}</div>
              <div className="w-full bg-brand-azure rounded-t" style={{ height: `${(t.visits / max) * 100}%`, minHeight: 4 }}></div>
              <div className="text-[10px] text-slate-500">{t.date.slice(5)}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-6 bg-white rounded-xl border border-[#E2E8F0] p-5">
        <div className="font-extrabold text-brand-navy">Uso AI</div>
        <div className="text-3xl font-extrabold text-brand-azure mt-1">{s.ai_actions}</div>
        <div className="text-xs text-slate-500">azioni AI registrate (dettatura + suggerimenti clinici)</div>
      </div>
    </div>
  );
}

function AuditTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/audit", { params: { limit: 100 } })
      .then(({ data }) => setItems(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const actionLabel = (a) => ({
    login: "Login",
    visit_created: "Visita creata",
    visit_updated: "Visita aggiornata",
    visit_closed: "Visita chiusa",
    visit_addendum: "Addendum",
    visit_report_sent: "Referto inviato",
    medico_created: "Medico creato",
    medico_suspended: "Medico sospeso",
    medico_active: "Medico riattivato",
    medico_deleted: "Medico eliminato",
    medico_updated: "Medico aggiornato",
    admin_clinical_access: "Accesso clinico admin",
    ai_dictation: "AI dettatura",
    ai_clinical_suggest: "AI suggerimento clinico",
  }[a] || a);

  return (
    <div>
      <h2 className="text-xl font-extrabold text-brand-navy mb-2">Audit log & Privacy</h2>
      <p className="text-sm text-slate-500 mb-4">L'admin centrale vede solo eventi (chi/quando/cosa). Il contenuto clinico delle visite NON è visualizzato qui — separazione tenant garantita.</p>
      {loading ? <div className="text-slate-500">Caricamento…</div> : (
        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden" data-testid="audit-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Quando</th>
                <th className="text-left px-4 py-3">Chi</th>
                <th className="text-left px-4 py-3">Ruolo</th>
                <th className="text-left px-4 py-3">Azione</th>
                <th className="text-left px-4 py-3">Target</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-[#E2E8F0]">
                  <td className="px-4 py-2.5 text-slate-500 text-xs whitespace-nowrap">{new Date(it.at).toLocaleString("it-IT")}</td>
                  <td className="px-4 py-2.5 text-slate-700">{it.actor_email || "—"}</td>
                  <td className="px-4 py-2.5"><span className="text-xs font-bold bg-slate-100 px-2 py-0.5 rounded uppercase tracking-wider text-slate-600">{it.actor_role}</span></td>
                  <td className="px-4 py-2.5 font-bold text-brand-navy">{actionLabel(it.action)}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">{it.target_type ? `${it.target_type}/${(it.target_id || "").slice(0, 8)}` : "—"}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Nessun evento</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="text-emerald-600 mt-0.5" />
          <div>
            <div className="font-extrabold text-emerald-800">Privacy by design: controlli tecnici implementati</div>
            <ul className="text-sm text-emerald-700 mt-1 space-y-1 list-disc pl-5">
              <li>Minimizzazione: bozza visita = nome/cognome + tipologia. Email/SMS solo se serve invio referto.</li>
              <li>Tenant isolation: ogni medico vede solo i propri pazienti e bozze.</li>
              <li>Audit log: ogni accesso clinico admin viene tracciato in questa pagina.</li>
              <li>Lock visite chiuse: solo addendum, nessuna modifica retroattiva.</li>
              <li>Audio/trascrizione raw: cancellati dopo 7 giorni, mantenuti solo i campi clinici strutturati.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function VisiteTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    const id = setTimeout(async () => {
      try { const { data } = await api.get("/admin/visits", { params: q ? { q } : {} }); setItems(data); } catch { /* noop */ }
    }, 250);
    return () => clearTimeout(id);
  }, [q]);
  return (
    <div>
      <h2 className="text-xl font-extrabold text-brand-navy mb-1">Tutte le visite del centro</h2>
      <p className="text-sm text-slate-500 mb-4">Vista segreteria — operativa, senza contenuto clinico esposto (OD/OS/anamnesi/diagnosi/terapia/referto restano protetti).</p>
      <input data-testid="adm-visite-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca paziente o medico…" className="mb-4 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]" />
      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-x-auto" data-testid="adm-visite-table">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
            <tr><th className="text-left px-4 py-3">Data</th><th className="text-left px-4 py-3">Medico</th><th className="text-left px-4 py-3">Paziente</th><th className="text-left px-4 py-3">CF</th><th className="text-left px-4 py-3">Tipo</th><th className="text-left px-4 py-3">Stato</th></tr>
          </thead>
          <tbody>
            {items.map((v) => (
              <tr key={v.id} data-testid={`adm-visit-${v.id}`} className="border-t border-[#E2E8F0]">
                <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{new Date(v.created_at).toLocaleDateString("it-IT")}</td>
                <td className="px-4 py-2.5 text-brand-navy font-bold">{v.doctor_name}</td>
                <td className="px-4 py-2.5">{v.patient_full_name}</td>
                <td className="px-4 py-2.5 text-slate-500 font-mono text-xs">{v.patient_fiscal_code || "—"}</td>
                <td className="px-4 py-2.5">{v.service}</td>
                <td className="px-4 py-2.5">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${v.status === "closed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{v.status === "closed" ? (v.report_sent_at ? "Referto inviato" : "Chiusa") : "Bozza"}</span>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Nessuna visita</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AppuntamentiTab() {
  const [items, setItems] = useState([]);
  const load = async () => { try { const { data } = await api.get("/admin/appointments"); setItems(data); } catch { /* noop */ } };
  useEffect(() => { load(); }, []);
  const sendReminder = async (a) => {
    try { await api.post(`/admin/appointments/${a.id}/send-reminder`); toast.success("Promemoria inviato"); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };
  return (
    <div>
      <h2 className="text-xl font-extrabold text-brand-navy mb-1">Appuntamenti del centro</h2>
      <p className="text-sm text-slate-500 mb-4">Tutti i medici, tutti gli appuntamenti. Puoi inviare promemoria 24h dalla segreteria.</p>
      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-x-auto" data-testid="adm-appts-table">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
            <tr><th className="text-left px-4 py-3">Quando</th><th className="text-left px-4 py-3">Medico</th><th className="text-left px-4 py-3">Paziente</th><th className="text-left px-4 py-3">Contatti</th><th className="text-left px-4 py-3">Stato</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} data-testid={`adm-appt-${a.id}`} className="border-t border-[#E2E8F0]">
                <td className="px-4 py-2.5 whitespace-nowrap">{new Date(a.starts_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</td>
                <td className="px-4 py-2.5 text-brand-navy font-bold">{a.doctor_name}</td>
                <td className="px-4 py-2.5">{a.patient_full_name}</td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{a.patient_phone || ""}{a.patient_phone && a.patient_email ? " · " : ""}{a.patient_email || ""}</td>
                <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${a.confirmation === "confirmed" ? "bg-emerald-100 text-emerald-700" : a.confirmation === "declined" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{a.confirmation}</span></td>
                <td className="px-4 py-2.5 text-right">
                  {(a.confirmation === "pending" || a.confirmation === "reminder_sent") && (a.patient_phone || a.patient_email) && (
                    <button data-testid={`adm-rem-${a.id}`} onClick={() => sendReminder(a)} className="text-[10px] font-bold bg-amber-100 hover:bg-amber-200 text-amber-700 px-2 py-1 rounded">Invia promemoria</button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Nessun appuntamento</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PazientiTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    const id = setTimeout(async () => {
      try { const { data } = await api.get("/admin/patients", { params: q ? { q } : {} }); setItems(data); } catch { /* noop */ }
    }, 250);
    return () => clearTimeout(id);
  }, [q]);
  return (
    <div>
      <h2 className="text-xl font-extrabold text-brand-navy mb-1">Pazienti del centro</h2>
      <p className="text-sm text-slate-500 mb-4">Indice anagrafico aggregato per medico curante.</p>
      <input data-testid="adm-pz-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, cognome, CF, telefono, email…" className="mb-4 w-full px-3 py-2 rounded-lg border border-[#E2E8F0]" />
      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-x-auto" data-testid="adm-pz-table">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
            <tr><th className="text-left px-4 py-3">Paziente</th><th className="text-left px-4 py-3">CF</th><th className="text-left px-4 py-3">Nascita</th><th className="text-left px-4 py-3">Contatti</th><th className="text-left px-4 py-3">Medico</th><th className="text-right px-4 py-3">Visite</th><th className="text-right px-4 py-3">Ultima</th></tr>
          </thead>
          <tbody>
            {items.map((p, i) => (
              <tr key={i} data-testid={`adm-pz-${i}`} className="border-t border-[#E2E8F0]">
                <td className="px-4 py-2.5 font-bold text-brand-navy">{p.patient_full_name}</td>
                <td className="px-4 py-2.5 text-slate-500 font-mono text-xs">{p.patient_fiscal_code || "—"}</td>
                <td className="px-4 py-2.5 text-slate-500">{p.patient_birth || "—"}</td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{p.patient_phone || ""}{p.patient_phone && p.patient_email ? " · " : ""}{p.patient_email || ""}</td>
                <td className="px-4 py-2.5">{p.doctor_name}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{p.visits_count}</td>
                <td className="px-4 py-2.5 text-right text-slate-500 text-xs whitespace-nowrap">{new Date(p.last_visit_at).toLocaleDateString("it-IT")}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Nessun paziente</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const STATUSES = ["nuovo", "letto", "in_valutazione", "risolto"];
const STATUS_STYLE = {
  nuovo: "bg-slate-100 text-slate-700",
  letto: "bg-blue-100 text-blue-700",
  in_valutazione: "bg-amber-100 text-amber-700",
  risolto: "bg-emerald-100 text-emerald-700",
};

function FeedbackTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterArea, setFilterArea] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status_ = filterStatus;
      if (filterArea) params.area = filterArea;
      const { data } = await api.get("/admin/feedback", { params });
      setList(data || []);
    } catch (e) { toast.error(apiErr(e, "Errore caricamento feedback")); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterStatus, filterArea]);

  const setStatus = async (f, status) => {
    try { await api.patch(`/admin/feedback/${f.id}/status`, { status }); toast.success("Stato aggiornato"); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-extrabold text-brand-navy">Feedback prodotto</h2>
          <p className="text-sm text-slate-500">Commenti, idee e segnalazioni inviate dai medici. <b>Nessun dato clinico:</b> solo suggerimenti sul software.</p>
        </div>
        <div className="flex gap-2">
          <select data-testid="admin-fb-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm bg-white">
            <option value="">Tutti gli stati</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="admin-fb-area" value={filterArea} onChange={(e) => setFilterArea(e.target.value)} className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm bg-white">
            <option value="">Tutte le aree</option>
            {["cartella","agenda","terapia","referti","ia","farmaci","impostazioni","altro"].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {loading ? <div className="text-slate-500 py-8 text-center">Caricamento…</div> : list.length === 0 ? (
        <div className="text-slate-500 italic py-8 text-center">Nessun feedback ricevuto.</div>
      ) : (
        <ul className="space-y-2" data-testid="admin-feedback-list">
          {list.map((f) => (
            <li key={f.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid={`admin-feedback-${f.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-brand-navy">{f.title}</div>
                  <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{f.body}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-2 flex flex-wrap gap-2 items-center">
                    <span>Da: <b className="text-slate-700">{f.doctor_name}</b> · {f.doctor_email}</span>
                    <span className="text-slate-300">·</span>
                    <span>{new Date(f.created_at).toLocaleString("it-IT")}</span>
                    <span className="text-slate-300">·</span>
                    <span>Area: <b className="text-slate-700">{f.area}</b></span>
                    {f.priority && <><span className="text-slate-300">·</span><span>Prio: <b className="text-slate-700">{f.priority}</b></span></>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold ${STATUS_STYLE[f.status] || ""}`}>{f.status}</span>
                  <select value={f.status} onChange={(e) => setStatus(f, e.target.value)} className="text-xs border border-[#E2E8F0] rounded px-2 py-1 bg-white" data-testid={`admin-fb-status-${f.id}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


export default function AdminSaaS() {
  const [tab, setTab] = useState("medici");
  return (
    <AppShell title="Prime Vision · Admin Primevision" subtitle="Gestione account medici, statistiche di utilizzo, audit amministrativo">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-900 flex items-start gap-2">
        <ShieldCheck size={14} className="mt-0.5 shrink-0" />
        <div><b>Privacy by design:</b> l'amministratore Primevision gestisce gli account medici e visualizza statistiche di utilizzo aggregate. Non ha accesso al database clinico (pazienti, cartelle, referti) dei medici — ogni medico è un tenant separato.</div>
      </div>
      <div className="flex gap-2 mb-6 border-b border-[#E2E8F0] overflow-x-auto">
        {[
          { k: "medici", label: "Medici", icon: Users },
          { k: "stats", label: "Statistiche utilizzo", icon: BarChart3 },
          { k: "audit", label: "Audit amministrativo", icon: ShieldCheck },
          { k: "feedback", label: "Feedback prodotto", icon: MessageSquare },
          { k: "messaging", label: "Messaggistica Brevo", icon: Mail },
          { k: "security", label: "Sicurezza", icon: Shield },
          { k: "backup", label: "Backup", icon: Database },
          { k: "compliance", label: "Compliance GDPR", icon: ListChecks },
          { k: "automations", label: "Automazioni", icon: Zap },
          { k: "fse", label: "FSE 2.0", icon: ShieldCheck },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => setTab(t.k)} className={`px-4 py-2.5 font-bold text-sm flex items-center gap-2 border-b-2 whitespace-nowrap transition ${tab === t.k ? "border-brand-navy text-brand-navy" : "border-transparent text-slate-500 hover:text-brand-navy"}`}>
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "medici" && <MediciTab />}
      {tab === "stats" && <StatsTab />}
      {tab === "audit" && <AuditTab />}
      {tab === "feedback" && <FeedbackTab />}
      {tab === "messaging" && <MessagingPanel />}
      {tab === "security" && <SecurityPanel />}
      {tab === "backup" && <BackupPanel />}
      {tab === "compliance" && <CompliancePanel />}
      {tab === "automations" && <AutomationsCenter mode="admin" />}
      {tab === "fse" && <FsePanel />}
    </AppShell>
  );
}
